<template>
    <Modal
        ref="upload"
        class="upload"
        width="1120px"
        hide-close
        :class="[viewport.is, variant === 'ai' && isUploading ? 'document' : variant, { steps: showSteps && !viewport.is.tablet }]"
        :focus-trap="false"
        :full="viewport.is.tablet"
        :body-padding="false"
        :prevent-backdrop="currentVariant === 'B'"
        @close="$emit('close')"
    >
        <template v-if="!$global.isHeadless">
            <Button
                v-if="currentVariant === 'B' && (prompt || isUploading || upload)"
                variant="basic"
                colour="translucent"
                size="xs" heading
                icon="arrow-left"
                icon-size="32"
                icon-pos="left"
                class="back"
                @click="
                    prompt = null;
                    upload = null;
                    $refs.converter && $refs.converter.cancel();
                "
            >
                Back
            </Button>
            <Button
                v-else-if="currentVariant !== 'B'"
                variant="basic"
                colour="translucent"
                size="xs" heading
                icon="arrow-left"
                icon-size="32"
                icon-pos="left"
                class="back"
                @click="$refs.upload.close('close button')"
            >
                Back
            </Button>
            <Button
                v-if="currentVariant !== 'B'"
                variant="basic"
                icon="cross"
                icon-size="32" 
                colour="translucent"
                :icon-label="$trans('close')"
                @click="$refs.upload.close('close button')"
            />
        </template>
        <Transition name="fade">
            <img
                v-if="variant === 'ai' && !isUploading"
                :src="url('/assets/animations/transform-ai.jpg')"
                alt=""
                role="presentation"
            />
        </Transition>
        <Transition name="fade">
            <span v-if="variant === 'document'">
                <Txt
                    size="xl" heading
                    align="center"
                    flex
                >
                    <Icon name="transform-document" size="48" right />
                    Document to a course
                </Txt>
                <GradientText
                    :size="viewport.is.tablet ? 'xs' : 'm'" heading
                    variant="lightblue"
                >
                    {{ headless ?
                        'Using the power of AI, transform your document into beautiful and engaging microlearning.' :
                        'Using the power of AI, Coassemble transforms your document into beautiful and engaging microlearning.'
                    }}
                </GradientText>
                <Converter
                    ref="converter"
                    @upload="upload = $event"
                />
            </span>
            <span v-else-if="(viewport.is.tablet && !isPromptSubmitted) || !viewport.is.tablet">
                <Txt
                    :size="viewport.is.tablet ? 'm' : 'xl'" heading
                    align="center"
                    flex
                >
                    <Icon :name="currentVariant === 'B' && !viewport.is.tablet ? 'edit' : 'magic'" :size="viewport.is.tablet ? '32' : '48'" right />
                    {{ currentVariant === 'B' && !viewport.is.tablet ? 'Create a course' : 'Generate a course' }}
                </Txt>
                <Txt v-if="currentVariant === 'A' || viewport.is.tablet || prompt" :size="viewport.is.tablet ? 'xs' : 'm'" heading>
                    {{ headless ?
                        'Using the power of AI, create a micro-learning course with just a description.' :
                        'Using the power of AI, Coassemble will create a micro-learning course with just a description.'
                    }}
                </Txt>
                <Txt v-else-if="upload || isUploading" :size="viewport.is.tablet ? 'xs' : 'm'" heading>
                    {{ headless ?
                        'Using the power of AI, transform your document into beautiful and engaging microlearning.' :
                        'Using the power of AI, Coassemble transforms your document into beautiful and engaging microlearning.'
                    }}
                </Txt>
                <Txt v-else :size="viewport.is.tablet ? 'xs' : 'm'" heading>
                    Use a prompt or upload a document to get started.
                </Txt>
                <form @submit.prevent="onPrompt">
                    <Field
                        v-if="!isUploading"
                        v-model:value="prompt"
                        type="textarea"
                        :rows="3"
                        placeholder="Describe what your course is about."
                        class="translucent-dark"
                        inline
                        :show-label="false"
                        @focus="() => {
                            if (currentVariant === 'B') $track.event('Course Creation Started', { 'course_creation_type': 'ai prompt' });
                            if (prompt || upload) return;
                            $track.event('AI Prompt Started');
                        }"
                    />
                    <div v-if="currentVariant === 'B' && !prompt && !isUploading && !viewport.is.tablet" class="separator">
                        <div />
                        <Txt size="xs" heading>
                            or
                        </Txt>
                        <div />
                    </div>
                    <Converter
                        v-if="currentVariant === 'B' && !prompt && !viewport.is.tablet"
                        ref="converter"
                        :animation="false"
                        @uploading="isUploading = true"
                        @upload="upload = $event"
                        @click="$track.event('Course Creation Started', { 'course_creation_type': 'transform' })"
                    />
                    <Button
                        v-if="currentVariant === 'A' || viewport.is.tablet || prompt"
                        type="submit"
                        variant="create"
                        colour="translucent-dark"
                        icon="correct"
                        icon-pos="right"
                        size="xxs" heading
                        :disabled="!prompt"
                        @click="$track.event('AI Prompt Saved', {
                            'ai_prompt': prompt,
                            'course_creation_type': 'ai prompt'
                        })"
                    >
                        Confirm
                    </Button>
                </form>
            </span>
        </Transition>
    </Modal>
    <Modal
        v-if="isShowingStepsModal"
        ref="transform"
        class="transform"
        width="1120px"
        :body-padding="false"
        :class="[viewport.is, { steps: showSteps }]"
        :focus-trap="false"
        :full="viewport.is.tablet"
        :drawer="viewport.is.tablet"
        :hide-close="currentVariant === 'B'"
        prevent-backdrop
        @close="onClose"
    >
        <template #header>
            <Button
                v-if="!viewport.is.tablet"
                variant="basic"
                colour="anchor"
                size="xs" heading
                icon="arrow-left"
                icon-pos="left"
                @click="currentStep === 0 ? back() : $refs.carousel.goToPreviousSlide()"
            >
                Back
            </Button>
            <Txt v-if="variant === 'document'" size="s" heading>
                <Icon name="transform-document" right />
                Document to a course
            </Txt>
            <Txt v-if="variant === 'ai'" size="s" heading>
                <Icon name="magic" right />
                Generate a course
            </Txt>
            <Txt v-if="variant === 'scratch'" size="s" heading>
                <Icon name="edit" right />
                Create a course from scratch
            </Txt>
        </template>
        <ProgressBar
            :value="currentStep"
            :max="$global.isLoggedIn || viewport.is.tablet ? 3 : (variant === 'scratch' ? 1 : 2)"
        />
        <Background />
        <div ref="observable" />
        <Carousel
            ref="carousel"
            :length="4"
            :slide-width="viewport.is.tablet ? viewport.width : component.width || 1120"
            disabled
            @select="currentStep = $event"
        >
            <section v-if="variant === 'document'">
                <div>
                    <Txt :size="viewport.is.tablet ? 's': 'm'" heading>
                        1. Give your course a title
                    </Txt>
                    <Field
                        v-model:value="title"
                        placeholder="Add your title here"
                        :show-label="false"
                        inline
                    />
                    <Button
                        variant="create"
                        icon="correct"
                        icon-pos="right"
                        size="xs" heading
                        :disabled="!title"
                        @click="
                            $refs.carousel.goToNextSlide();
                            $track.event('Course Title Saved', {
                                'course_creation_type': 'transform',
                                'course_title': title,
                                'course_title_edited': upload && title !== upload.name.replace(/\.[^/.]+$/, ''),
                            })
                        "
                    >
                        Confirm
                    </Button>
                </div>
            </section>
            <section v-if="variant === 'scratch'">
                <div>
                    <Txt :size="viewport.is.tablet ? 's': 'm'" heading>
                        1. What's your course about?
                    </Txt>
                    <Field
                        v-model:value="prompt"
                        placeholder="In just a few words, describe what your course is about. "
                        :show-label="false"
                        inline
                    />
                    <div class="button-wrapper">
                        <Button
                            variant="create"
                            icon="correct"
                            icon-pos="right"
                            size="xs" heading
                            :disabled="!prompt"
                            @click="
                                generateOutline();
                                $refs.carousel.goToNextSlide();
                                $track.event('Course Topic Saved', {
                                    'course_topic': prompt,
                                    'course_creation_type': 'from scratch'
                                })
                            "
                        >
                            Confirm
                        </Button>
                        <Txt v-if="prompt" size="xs" flex class="text-primary">
                            <Icon name="magic" size="16" right />
                            We’ll use this information to suggest a title and course overview to help you get started.
                        </Txt>
                    </div>
                </div>
            </section>
            <section v-else-if="currentVariant === 'A' || $global.isLoggedIn || headless">
                <div>
                    <Txt :size="viewport.is.tablet ? 's': 'm'" heading>
                        {{ variant === 'ai' ? '1' : '2' }}. What style of course do you want to create?
                    </Txt>
                    <div class="style-radios">
                        <Radio
                            v-model:value="style"
                            name="style"
                            radio-value="brief"
                            @click="style = 'brief'"
                        >
                            <Txt size="xs" heading>
                                Straight to the point 🏄
                            </Txt>
                            <Txt v-if="variant === 'document'" size="s">
                                Summarize the source text down to the key points only. No fluff, no fuss, just the fundamentals.
                            </Txt>
                        </Radio>
                        <Radio
                            v-model:value="style"
                            name="style"
                            radio-value="balanced"
                            @click="style = 'balanced'"
                        >
                            <Txt size="xs" heading>
                                Balanced 🧘
                            </Txt>
                            <Txt v-if="variant === 'document'" size="s">
                                We’ll condense the source text with care, exposing the key concepts, and more.
                            </Txt>
                        </Radio>
                        <Radio
                            v-model:value="style"
                            name="style"
                            radio-value="comprehensive"
                            @click="style = 'comprehensive'"
                        >
                            <Txt size="xs" heading>
                                Comprehensive 🤓
                            </Txt>
                            <Txt v-if="variant === 'document'" size="s">
                                We'll stay as true to the source text as possible.
                            </Txt>
                        </Radio>
                    </div>
                    <Button
                        variant="create"
                        icon="correct"
                        icon-pos="right"
                        size="xs" heading
                        :disabled="!style"
                        @click="
                            $refs.carousel.goToNextSlide();
                            $track.event('Course Style Saved', {
                                'course_creation_type': variant === 'ai' ? 'ai prompt' : 'transform',
                                'ai_prompt': prompt,
                                'course_title': title,
                                'course_style': style,
                            })
                        "
                    >
                        Confirm
                    </Button>
                </div>
            </section>
            <section v-if="variant === 'ai' && (currentVariant === 'A' || $global.isLoggedIn || headless)">
                <div>
                    <Txt :size="viewport.is.tablet ? 's': 'm'" heading>
                        2. What should the tone of the course be?
                    </Txt>
                    <div class="style-radios">
                        <Radio
                            v-model:value="tone"
                            name="tone"
                            radio-value="casual"
                            @click="tone = 'casual'"
                        >
                            <Txt size="xs" heading>
                                Casual 🩴
                            </Txt>
                        </Radio>
                        <Radio
                            v-model:value="tone"
                            name="tone"
                            radio-value="energetic"
                            @click="tone = 'energetic'"
                        >
                            <Txt size="xs" heading>
                                Energetic ⚡️
                            </Txt>
                        </Radio>
                        <Radio
                            v-model:value="tone"
                            name="tone"
                            radio-value="professional"
                            @click="tone = 'professional'"
                        >
                            <Txt size="xs" heading>
                                Professional 👔
                            </Txt>
                        </Radio>
                    </div>
                    <Button
                        variant="create"
                        icon="correct"
                        icon-pos="right"
                        size="xs" heading
                        :disabled="!tone"
                        @click="
                            $refs.carousel.goToNextSlide();
                            $track.event('Course Tone Saved', {
                                'course_creation_type': variant === 'ai' ? 'ai prompt' : 'transform',
                                'ai_prompt': prompt,
                                'course_title': title,
                                'course_tone': tone
                            });
                        "
                    >
                        Confirm
                    </Button>
                </div>
            </section>
            <section v-if="$global.isLoggedIn || (viewport.is.tablet && currentVariant === 'A')">
                <div v-if="currentStep === 2 || (variant === 'scratch' && currentStep === 1)">
                    <Txt v-if="variant === 'ai'" :size="viewport.is.tablet ? 's': 'm'" heading>
                        3. Select a course theme that best suits your course.
                    </Txt>
                    <Txt v-else-if="variant === 'scratch'" :size="viewport.is.tablet ? 's': 'm'" heading>
                        2. Select a course theme that best suits the tone of your course.
                    </Txt>
                    <Txt v-else :size="viewport.is.tablet ? 's': 'm'" heading>
                        3. Select a course theme that best suits the tone of your documents content.
                    </Txt>
                    <Carousel
                        ref="themes"
                        :slide-width="280"
                        :length="themes.length"
                        :transform="!$browser.is.ios"
                        navigation
                    >
                        <Clickable
                            v-for="t, themeIndex in themes"
                            :key="t.id"
                            class="theme-card"
                            :class="{ selected: theme === t }"
                            :style="{ '--background': t.colours.standard.primary }"
                            @click="
                                theme = t;
                                $refs.themes.goToSlide(themeIndex);
                            "
                        >
                            <div class="theme-card-body">
                                <ScreenPreview
                                    v-for="(screen, index) in previewScreens"
                                    :key="index"
                                    :screen="screen"
                                    :course="{ theme: { ...t, preset: t.id } }"
                                    :finish="screen.finish"
                                    :style="{ zIndex: index }"
                                />
                            </div>
                            <div class="theme-card-label">
                                <Txt size="xxs" heading>
                                    {{ t.label }}
                                </Txt>
                                <Icon v-if="theme === t" name="correct-circle" class="text-primary" />
                            </div>
                        </Clickable>
                    </Carousel>
                    <Button
                        variant="create"
                        icon="correct"
                        icon-pos="right"
                        size="xs" heading
                        :disabled="!theme"
                        @click="
                            $refs.carousel.goToNextSlide();
                            $track.event('Course Theme Saved', {
                                'course_creation_type': courseCreationType,
                                [variant === 'scratch' ? 'course_topic' : 'ai_prompt']: prompt,
                                'course_title': title,
                                'course_theme': theme.id
                            });
                        "
                    >
                        Confirm
                    </Button>
                </div>
            </section>
            <section v-if="variant === 'scratch' && $global.isLoggedIn">
                <div v-if="currentStep === 2">
                    <Txt :size="viewport.is.tablet ? 's': 'm'" heading>
                        3. Choose a layout for the course card and start screen.
                    </Txt>
                    <Carousel
                        ref="layouts"
                        :slide-width="280"
                        :length="layouts.length"
                        navigation
                    >
                        <Clickable
                            v-for="l, layoutIndex in layouts"
                            :key="l.id"
                            class="theme-card"
                            :class="{ selected: layout === l }"
                            @click="
                                layout = l;
                                $refs.layouts.goToSlide(layoutIndex);
                            "
                        >
                            <ScreenPreview
                                :screen="{
                                    ...layoutScreen,
                                    object: {
                                        ...layoutScreen.object,
                                        mode: l.mode,
                                        layout: l.option === 'background' ? 'background' : l.mode,
                                        drag_index: l.mode === 'inline' ? 1 : 0,
                                        text_alignment: l.mode === 'inline' || l.mode === 'inline-reverse' ? 'left' : 'center',
                                        overlay: 0.5
                                    }
                                }"
                                :course="{ theme: { ...theme, preset: theme.id } }"
                                :finish="layoutScreen.finish"
                            />
                        </Clickable>
                    </Carousel>
                    <Button
                        variant="create"
                        icon="correct"
                        icon-pos="right"
                        size="xs" heading
                        :disabled="!layout"
                        @click="
                            $refs.carousel.goToNextSlide();
                            $track.event('Course Image Option Saved', {
                                'course_creation_type': courseCreationType,
                                'course_topic': prompt,
                                'image_option': layout.id
                            });
                        "
                    >
                        Confirm
                    </Button>
                </div>
            </section>
            <section class="final" :style="{ '--height': `${viewport.height - 88}px` }">
                <div v-if="isConverted">
                    <Animation
                        :src="variant === 'document' ? 'transform-complete' : 'loader-success'"
                        width="120"
                        height="120"
                        :loop="false"
                    />
                    <Txt v-if="variant === 'document'" size="m" heading>
                        Document ready for transformation!
                    </Txt>
                    <Txt v-if="viewport.is.tablet" size="s" heading>
                        Your course is ready!
                    </Txt>
                    <Txt v-else-if="variant === 'ai'" size="m" heading>
                        Your course is ready to be created!
                    </Txt>
                    <Txt v-if="variant === 'scratch'" size="m" heading>
                        One last thing...
                    </Txt>
                    <Txt v-if="variant === 'scratch'">
                        To help you on your way, we suggested a <strong>title</strong> and <strong>introduction</strong> for your course. Edit these as you like, and continue building out your course by clicking the <strong>‘Add screen’</strong> button.
                    </Txt>
                    <Txt v-if="viewport.is.tablet" size="l">
                        Preview the entire course to sign up!
                    </Txt>
                    <div
                        v-if="images && images.length && $global.isLoggedIn"
                        class="images-toggle"
                        :class="{ active: $global.isLoggedIn }"
                    >
                        <SwitchField
                            v-model:value="saveImages"
                            left
                            :disabled="!$global.isLoggedIn"
                        >
                            <div class="images-toggle-label">
                                <Txt size="xxs" heading flex>
                                    Transfer images to <Icon name="image-multiple" size="18" /> Image library
                                </Txt>
                                <Txt size="xs">
                                    Images within your document will automatically be added to your Image library. If your document has many images, this process may take a while to complete.
                                </Txt>
                            </div>
                        </SwitchField>
                    </div>
                    <Button
                        v-if="!(variant === 'ai' && $global.isLoggedIn)"
                        variant="create"
                        icon="arrow-right"
                        icon-pos="right"
                        size="xs" heading
                        :status="status"
                        @click="createCourse"
                    >
                        {{ viewport.is.tablet ? 'Preview course' : (variant === 'scratch' || currentVariant === 'B' ? 'Let\'s go' : 'Create course') }}
                    </Button>
                </div>
                <div v-else>
                    <Animation
                        :src="variant === 'document' ? 'loader-transform' : 'paint'"
                        width="120"
                        height="120"
                    />
                    <Txt v-if="variant === 'document'" size="m" heading>
                        Finalizing your document for transformation...
                    </Txt>
                    <Txt v-if="viewport.is.tablet" size="s" heading>
                        Building your course
                    </Txt>
                    <Txt v-else-if="variant === 'ai'" size="m" heading>
                        Generating your course outline...
                    </Txt>
                    <Txt v-if="variant === 'scratch'" size="m" heading>
                        One last thing...
                    </Txt>
                    <Txt v-if="viewport.is.tablet && title" size="l">
                        Generating screen {{ generatedTotal }}/{{ generateTotal }}
                    </Txt>
                    <Txt v-else-if="viewport.is.tablet" size="l">
                        Generating course outline
                    </Txt>
                    <Txt v-else-if="variant === 'scratch'">
                        To help you on your way, we suggested a <strong>title</strong> and <strong>introduction</strong> for your course. Edit these as you like, and continue building out your course by clicking the <strong>‘Add screen’</strong> button.
                    </Txt>
                    <Txt v-else size="l">
                        This may take a few seconds.
                    </Txt>
                    <Button
                        v-if="variant === 'scratch'"
                        variant="create"
                        icon="arrow-right"
                        icon-pos="right"
                        size="xs" heading
                        disabled
                        @click="createCourse"
                    >
                        Let's go
                    </Button>
                </div>
                <div v-if="viewport.is.tablet" class="tip-carousel">
                    <Carousel
                        ref="tips"
                        :slide-width="viewport.width - 48"
                        :length="3"
                        infinite
                        @select="selectedBannerIndex = $event"
                    >
                        <div class="tip">
                            <Icon name="preview" />
                            <Txt size="s">
                                Upon completing the course generation, you will have the ability to preview it.
                            </Txt>
                        </div>
                        <div class="tip">
                            <Icon name="device-desktop" />
                            <Txt size="s">
                                {{ headless ?
                                    'For the course builder, please access from a desktop computer.' :
                                    'For the course builder, please access Coassemble from a desktop computer.'
                                }}
                            </Txt>
                        </div>
                        <div class="tip">
                            <Icon name="save" />
                            <Txt size="s">
                                Should you wish to save this course, preview the complete course and sign up.
                            </Txt>
                        </div>
                    </Carousel>
                    <div class="pagination">
                        <div
                            v-for="(_, index) in 3"
                            :key="index"
                            :class="{ active: index === selectedBannerIndex }"
                            class="pagination-dot"
                        />
                    </div>
                </div>
            </section>
        </Carousel>
    </Modal>
</template>

<script>
import { url, sequence } from '@/helpers/utilities';
import { presets } from '@/helpers/theme';
import { startScreen } from '@/helpers/course';
import { images, quotes } from '@/helpers/placeholders';
import { useResponsive, getElement } from '@/store/responsive';
import { getItemKeyByType, getImageKeyByType } from '@/helpers/tile';

import useExperiments from '@/store/experiments';

import Txt from '@/components/form/txt';
import Field from '@/components/form/field';
import Radio from '@/components/form/radio';
import Icon from '@/components/utility/icon';
import Modal from '@/components/utility/modal';
import Button from '@/components/utility/button';
import Clickable from '@/components/utility/clickable';
import SwitchField from '@/components/form/switch-field';
import Carousel from '@/components/interactive/carousel';
import Animation from '@/components/animation/animation';
import ProgressBar from '@/components/progress/progress-bar';
import ScreenPreview from '@/components/author/screen-preview';
import GradientText from '@/components/animation/gradient-text';
import Converter from '@/pages/student/modules/lesson/converter';

import Background from '@/assets/transform/background.svg';

export default  {
    name: 'TransformFlow',
    components: { Txt, Field, Radio, Icon, Modal, Button, Clickable, SwitchField, Carousel, Animation, ProgressBar, ScreenPreview, GradientText, Converter, Background },
    inject: ['$global'],
    props: {
        variant: { type: String, default: 'document', options: ['document', 'ai', 'scratch'] },
        folder: { type: Object, default: null },
        builder: { type: Boolean, default: false },
        headless: { type: Boolean, default: false }
    },
    emits: ['close', 'course'],
    data() {
        return {
            course: null,
            screens: null,
            upload: null,

            currentStep: 0,
            selectedBannerIndex: 0,
            prompt: undefined,
            title: undefined,
            description: '',
            style: null,
            tone: null,
            theme: null,
            layout: null,
            suggestedImage: null,
            images: null,
            saveImages: false,

            generatedTotal: 0,
            generateTotal: 0,
            screenTypes: [],

            themes: presets.filter(preset => preset.id !== 'coassemble'),
            layouts: [
                { id: 'no image', option: 'full', mode: 'inline-full' },
                { id: 'image behind', option: 'background', mode: 'inline-full' },
                { id: 'image left', option: 'left', mode: 'inline' },
                { id: 'image right', option: 'right', mode: 'inline-reverse' },
            ],

            status: null,
            isConverted: false,
            isUploading: false,
            isFlowComplete: false,
            isPromptSubmitted: false,
            viewport: useResponsive()
        };
    },
    computed: {
        component() {
            return getElement(this);
        },
        layoutScreen() {
            const image = images[Math.floor(Math.random() * images.length)];
            return { ...startScreen({ title: 'Course title', duration: 24, description: 'Easy, stylish ways that make content engaging' }), asset: image };
        },
        previewScreens() {
            const quote = quotes[Math.floor(Math.random() * quotes.length)];
            const image = images[Math.floor(Math.random() * images.length)];
            return [
                { ...startScreen({ title: 'Theme', duration: 24, description: 'Easy, stylish ways that make content engaging' }), asset: null },
                { type: { name: 'textAndImage' }, asset: image, description: quote, object: { 'display_description': true } }
            ];
        },
        coursePayload() {
            const layout = this.layout ? {
                'mode': this.layout.mode,
                'layout': this.layout.option,
                'drag_index': this.layout.mode === 'inline' ? 1 : 0,
                'text_alignment': this.layout.mode === 'inline' || this.layout.mode === 'inline-reverse' ? 'left' : 'center',
                'overlay': 0.5
            } : {};
            return {
                id: this.course && this.course.id,
                active: true,
                title: this.title,
                description: this.description,
                screens: this.screens || undefined,
                start: {
                    'display_duration': false,
                    ...layout
                },
                image: this.suggestedImage,
                folder: this.folder && this.folder.id,
                theme: this.theme ? { ...this.theme, preset: this.theme.id } : presets[0],
                default: false
            };
        },
        hasCourseStyle() {
            return this.style && this.showSteps;
        },
        showSteps() {
            if (this.variant === 'scratch') return true;
            return !!this.upload || this.isPromptSubmitted;
        },
        courseCreationType() {
            return {
                document: 'transform',
                ai: 'ai prompt',
                scratch: 'from scratch'
            }[this.variant];
        },
        isShowingStepsModal() {
            if (!this.viewport.is.tablet) return true;
            return this.isPromptSubmitted || this.upload;
        },
        currentVariant() {
            if (!this.$route.path.includes('/build')) return 'A';
            return useExperiments.state
                && useExperiments.state.activeExperiment
                && useExperiments.state.activeExperiment.currentVariant;
        }
    },
    watch: {
        upload(to) {
            this.title = to.name.replace(/\.[^/.]+$/, '');
            if (this.hasCourseStyle) this.convertFile();
        },
        hasCourseStyle(to) {
            if (to) {
                if (this.upload) this.convertFile();
                if (this.prompt) this.generateOutline();
            }
        },
        isConverted(to) {
            if (this.variant !== 'ai') return;
            if (this.viewport.is.tablet) return;
            if (to && this.currentStep === 3) this.createCourse();
        },
        currentStep(to) {
            if (this.variant !== 'ai') return;
            if (to === 3 && this.isConverted) this.createCourse();
        },
    },
    mounted() {
        this.getTileTypes();
        this.$timeout(() => useResponsive(this, this.$refs.observable), 1000);

        if (this.currentVariant === 'B' && !this.$global.isLoggedIn) {
            this.style = 'brief';
            this.tone = 'professional';
        }
        if (this.viewport.is.tablet) {
            setInterval(() => {
                if (this.$refs.tips) this.$refs.tips.goToNextSlide();
            }, 5000);
        }
    },
    beforeUnmount() {
        useResponsive.removeElement(this);
    },
    methods: {
        url,
        convertFile() {
            return this.$http.post('/assistant/convert', { ...this.upload, style: this.style })
                .then(this.onGenerateResponse);
        },
        onPrompt() {
            this.isPromptSubmitted = true;
        },
        getTileTypes() {
            return this.$http.get('/course/types')
                .then(({ data }) => this.screenTypes = data)
                .catch(() => null);
        },
        generateOutline() {
            this.$http.get('/assistant/course', { params: { prompt: this.prompt, style: this.style, tone: this.tone, variant: this.variant } })
                .then(this.onGenerateResponse);
        },
        onGenerateResponse({ data }) {
            if (data.images) {
                this.images = data.images;
                this.saveImages = this.$global.isLoggedIn;
            }
            const result = data.result;
            const screens = result.screens;
            if (this.variant !== 'document') this.title = result.title;
            this.description = result.description;
            if (this.viewport.is.tablet) {
                this.generateScreens(screens)
                    .then(() => this.isConverted = true);
            } else if (this.variant === 'scratch') {
                this.getScreenImage(screens[0]).then(() => {
                    this.isConverted = true;
                    localStorage.setItem('coassemble:transform:outline', JSON.stringify(screens));
                });
            } else {
                this.isConverted = true;
                localStorage.setItem('coassemble:transform:outline', JSON.stringify(screens));
            }
        },
        generateScreens(screens) {
            this.screens = [];
            this.generateTotal = screens && screens.length || 0;
            this.generatedTotal = 1;

            return sequence(screens, (screen, index) => this.createGeneratedScreen(screen, index + 1));
        },
        async createGeneratedScreen(screen, seq) {
            let result;
            let data;
            try {
                const response = await this.$http.post('/assistant/screen', screen);
                data = response.data;
                result = data.result;
            } catch {
                this.generateTotal -= 1;
                this.generatedTotal -= 1;
                this.$track.event('AI Generation Failed', {
                    'ai_response': data
                });
                return Promise.resolve();
            }

            const typeName = {
                checklist: 'iaChecklist',
                flashcards: 'caseStudy',
                process: 'process',
                list: 'iaAcronym',
                text: 'textAndImage'
            }[screen.type] || 'textAndImage';
            const type = this.screenTypes.find(({ name }) => name === typeName);
            const _screen = {
                title: screen.title,
                type,
                sequence: seq,
                object: {
                    'legacy': false,
                    'display_title': true,
                    'display_description': screen.type === 'text' ? true : false,
                    'display_duration': false,
                    'display_button': false,
                    'display_image': false
                }
            };
            if (screen.type === 'text') {
                _screen.description = result.description;
                _screen.asset = await this.getSuggestedImage(result.keyword || screen.title);
                const modes = ['inline', 'inline-reverse', 'inline-full'];
                const mode = modes[Math.floor(Math.random() * modes.length)];
                _screen.object['mode'] = mode;
                _screen.object['drag_index'] = mode === 'inline-reverse' ? 2 : 1;
            } else {
                const key = getItemKeyByType(typeName);
                _screen.object[key] = result.items;
                if (screen.type === 'process') {
                    _screen.object[key].forEach(async item => {
                        item[getImageKeyByType(typeName)] = await this.getSuggestedImage(item.keyword || item.title);
                    });
                }
            }
            
            this.screens.push(_screen);
            this.generatingSequence += 1;
            this.generatedTotal += 1;
        },
        getRandomImage() {
            return images[Math.floor(Math.random() * images.length)];
        },
        getCourseImage(title) {
            return this.getSuggestedImage(title)
                .then(data => this.suggestedImage = data);
        },
        getScreenImage(screen) {
            return this.getSuggestedImage(screen.title)
                .then(data => screen.asset = data);
        },
        getSuggestedImage(q) {
            return this.$http.get(`/image/suggest`, { params: { q } })
                .then(({ data }) => data && data.link || null);
        },
        async createCourse() {
            this.status = 'waiting';
            if (!this.$global.isLoggedIn) return this.onSuccess();
            if (this.saveImages) localStorage.setItem('coassemble:transform:images', JSON.stringify(this.images));
            return this.$http
                .post('/course', this.coursePayload)
                .then(({ data }) => {
                    this.course = data;
                    this.onSuccess();
                })
                .catch(() => null);
        },
        onSuccess() {
            this.status = 'success';
            this.$track.event('Course Setup Finished', {
                'course_creation_type': this.courseCreationType,
                [this.variant === 'scratch' ? 'course_topic' : 'ai_prompt']: this.prompt,
                'course_title': this.title,
                'course_title_edited': this.upload && this.title !== this.upload.name.replace(/\.[^/.]+$/, ''),
                'course_tone': this.tone,
                'course_style': this.style,
                'image_option': this.layout && this.layout.id,
                'course_theme': this.theme && this.theme.id,
                'document_format': this.upload && this.upload.type,
            });
            localStorage.setItem('coassemble:transform:context', JSON.stringify({
                'course_creation_type': this.courseCreationType,
                [this.variant === 'scratch' ? 'course_topic' : 'ai_prompt']: this.prompt,
                'course_tone': this.tone,
                'course_style': this.style,
                'image_option': this.layout && this.layout.id,
                'course_title_edited': this.upload && this.title !== this.upload.name.replace(/\.[^/.]+$/, ''),
                'document_format': this.upload && this.upload.type,
            }));

            let target = '';
            if (this.builder) {
                this.$emit('course', { ...this.coursePayload, screens: this.screens || [], finish: {} });
            } else if (this.$global.isLoggedIn) {
                this.$emit('course', { ...this.coursePayload, screens: this.screens || [], finish: {} });
                target = `/course/${this.course && this.course.id}`;
            } else {
                localStorage.setItem('coassemble:tempcourse', JSON.stringify(this.coursePayload));
                target = '/build';
            }
            this.$timeout(() => {
                this.status = null;
                this.isFlowComplete = true;
                this.$refs.transform.close();
                if (target) this.$timeout(() => this.$router.push(target), 1000);
            }, 2000);
        },
        onClose() {
            if (!this.isFlowComplete) {
                localStorage.removeItem('coassemble:transform:context');
                localStorage.removeItem('coassemble:transform:outline');
            }
            this.$emit('close');
        },
        back() {
            this.isPromptSubmitted = false;
            this.$refs.upload.open();
        }
    }
};
</script>

<style lang="less">
.modal-outer.upload {
    --body: 'Roboto';
    transition: var(--smooth-slow);
    background-color: ~"rgba(var(--coal-rgb), 0.98)";
    &.steps { transform: translateX(-100%); }
    .modal-inner > .body {
        > img {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            z-index: 0;
        }
        .cobutton.basic {
            position: absolute;
            top: 0;
            right: 0;
            z-index: 1;
            margin: 24px;
            &.back {
                right: unset;
                left: 0;
            }
        }
        > span {
            padding: 96px 128px;
            min-height: 600px;
            text-align: center;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: #FFF;

            > .txt-component.heading {
                margin-bottom: 24px;
                &.m {
                    margin-bottom: 32px;
                    max-width: 760px;
                }
                .txt-component-content {
                    justify-content: center;
                }
            }
        }

        .separator {
            position: relative;
            display: flex;
            align-items: center;
            margin: 24px 0;
            .txt-component {
                text-transform: uppercase;
                margin: 0 8px;
                color: rgba(255, 255, 255, 0.5);
            }
            > div {
                height: 1.5px;
                flex: 1 1 auto;
                background-color: rgba(255, 255, 255, 0.12);
            }
        }
    }
    &.document {
        .modal-inner > .body {
            background: linear-gradient(293deg, var(--primary) -6.5%, var(--navy) 103.63%);
            form {
                width: 100%;
            }
        }
    }
    &.ai {
        .modal-inner > .body {
            .txt-component { position: relative; }
            form {
                width: 100%;
                .field.inline .field-container {
                    border-radius: 16px;
                    border: 1px solid ~"rgba(var(--coal-rgb), 0.25)";
                    &.focus { border-color: ~"rgba(var(--coal-rgb), 0.75)"; }
                    input, textarea {
                        padding: 16px;
                        font-weight: 500;
                        font-size: 16px;
                        height: 56px;
                        &::placeholder {
                            font-family: var(--heading);
                        }
                    }
                }
            }
        }
        .converter {
            height: 200px;
        }
        .cobutton.create {
            position: relative;
            margin-top: 24px;
            padding: 12px 24px;
            border-radius: 48px;
            height: auto;
        }
    }
    &.tablet {
        .modal-inner > .body > span {
            padding: 24px;
            .cobutton.basic {
                margin: 16px;
            }
            > .txt-component.heading.m { margin-bottom: 16px; }
            form {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                .field {
                    width: 100%;
                }
            }
        }
    }
}
.modal-outer.transform {
    --body: 'Roboto';
    transition: var(--smooth-slow);
    transform: translateX(100%);
    background-color: ~"rgba(var(--coal-rgb), 0.98)";
    &.steps, &.tablet { transform: translateX(0); }
    .modal-inner > .body {
        position: relative;
        background-color: var(--cloud);
        height: 592px;
        --progress-background-colour: var(--primary-tint);
        .progress {
            position: absolute;
            top: 0;
            left: -12px;
            right: -12px;
            z-index: 1;
            .progress-bar {
                background: linear-gradient(90deg, var(--navy) 0%, var(--primary) 100%);
            }
        }
        > svg {
            position: absolute;
            top: 0;
            left: 0;
        }
        .carousel {
            position: relative;
            height: 100%;
            section {
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
                > div {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: flex-start;
                    max-width: 896px;
                    width: 100%;
                    gap: 32px;
                    padding: 0 48px;
                    .button-wrapper {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }
                    .cobutton.create {
                        padding: 12px 24px;
                        height: auto;
                        border-radius: 1000px;
                    }
                    .field {
                        width: 100%;
                        .field-container {
                            border-radius: 16px;
                            input {
                                padding: 20px 16px;
                                font-weight: 600;
                                font-size: 16px;
                            }
                        }
                    }
                    .style-radios {
                        display: flex;
                        flex-direction: column;
                        gap: 16px;
                        width: 100%;
                    }
                    .radio-component {
                        padding: 20px 16px;
                        border-radius: 16px;
                        background-color: #FFF;
                        box-shadow: var(--shadow-base);
                        transition: var(--smooth);
                        .slot-wrapper {
                            padding: 0 8px 0 36px;
                            display: flex;
                            flex-direction: column;
                            gap: 4px;
                        }
                        &:hover {
                            box-shadow: var(--shadow-lg);
                        }
                    }
                    .carousel {
                        width: 1120px;
                        margin: 0 calc((1120px - 800px) / 2 * -1);
                        padding: 0 calc((1120px - 800px) / 2);
                        overflow: visible;
                        .carousel-inner {
                            gap: 16px;
                            padding: 0;
                        }
                    }
                    .images-toggle {
                        padding: 16px;
                        border: 1px solid var(--mercury);
                        color: var(--steel);
                        border-radius: 16px;
                        .icon { margin: 0 4px; }
                        .switch-field {
                            align-items: flex-start;
                            justify-content: space-between;
                        }
                        .images-toggle-label {
                            display: flex;
                            flex-direction: column;
                            gap: 8px;
                            max-width: 342px;
                            text-align: left;
                        }
                        .switch {
                            opacity: 0.5;
                            transform: scale(0.75);
                            transform-origin: top right;
                            margin: 0;
                        }
                        &.active {
                            border: 0;
                            background-color: var(--smoke);
                            color: var(--coal);
                            .switch { opacity: 1; }
                        }
                    }
                    .theme-card {
                        position: relative;
                        display: flex;
                        flex-direction: column;
                        height: 180px;
                        flex-basis: 264px;
                        box-shadow: var(--shadow-base);
                        transition: var(--smooth);
                        border-radius: 12px;
                        background-color: var(--background, #FFF);
                        overflow: hidden;
                        .theme-card-body {
                            position: relative;
                            flex: 1;
                            .screen-preview {
                                width: 142px;
                                height: 96px;
                                border-radius: 8px;
                                top: unset;
                                left: unset;
                                bottom: 16px;
                                right: 16px;
                                &:first-of-type {
                                    top: 16px;
                                    left: 16px;
                                }
                            }
                        }
                        .theme-card-label {
                            padding: 10px 12px;
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            background-color: #FFF;
                            height: 40px;
                        }
                        &.selected {
                            outline: 1px solid var(--primary);
                            box-shadow: var(--shadow-lg);
                        }
                        &:hover {
                            box-shadow: var(--shadow-lg);
                        }
                    }
                }
                &.final {
                    > div {
                        gap: 16px;
                        align-items: center;
                        text-align: center;
                    }
                }
            }
        }
    }
    &.tablet {
        background: none;
        .modal-inner > .body .carousel section {
            > div {
                padding: 0 24px;
            }
            &.final {
                height: var(--height);
                flex-direction: column;
                justify-content: space-between;
                padding: 32px 0;
                > div {
                    gap: 8px;
                }
            }
            .tip-carousel {
                display: flex;
                flex-direction: column;
                gap: 8px;
                background-color: var(--smoke);
                border-radius: 16px;
                margin: 0 24px;
                width: calc(100% - 48px);
                padding: 16px 0;
                overflow: hidden;
                .carousel {
                    width: 100%;
                    margin: 0;
                    padding: 0;
                    .carousel-inner { gap: 0; }
                    .tip {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        gap: 8px;
                        padding: 0 16px;
                        font-style: italic;
                    }
                }
                .pagination {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 12px;
                    padding: 8px 0;
                    .pagination-dot {
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                        background-color: var(--steel);
                        &.active { background-color: var(--anchor); }
                    }
                }
            }
        }
    }
}
</style>
